import { useNavigate } from "react-router-dom";
import { fmt, isZero } from "@/lib/format";

const Kpi = ({ label, value, testId, tone = "bg-white" }) => (
  <div className={`p-4 border border-slate-300 ${tone}`}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] opacity-70">{label}</p>
    <p className="font-mono text-xl sm:text-2xl font-semibold tabular-nums tracking-tight mt-1" data-testid={testId}>{value}</p>
  </div>
);

export const BooksDashboard = ({ books }) => {
  const nav = useNavigate();
  const totalAvail = books.reduce((a, b) => a + b.closing_available, 0);
  const totalMis = books.reduce((a, b) => a + b.mismatches, 0);
  const totalPnl = books.reduce((a, b) => a + b.total_pnl, 0);
  const max = Math.max(1, ...books.map((b) => Math.abs(b.closing_available)));

  return (
    <section className="space-y-4 mb-12 fade-up" data-testid="books-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Available · all books" value={fmt(totalAvail)} testId="dash-total-available" tone="bg-cyan-100 text-cyan-950" />
        <Kpi label="Books" value={books.length} testId="dash-book-count" />
        <Kpi label="Difference · all time" value={fmt(totalPnl)} testId="dash-total-pnl"
          tone={isZero(totalPnl) ? "bg-green-100 text-green-900" : "bg-red-100 text-red-800"} />
        <Kpi label="Mismatched rows" value={totalMis} testId="dash-total-mismatches"
          tone={totalMis ? "bg-red-100 text-red-800" : "bg-green-100 text-green-900"} />
      </div>
      <div className="bg-white border border-slate-300">
        <div className="px-4 py-2.5 border-b border-slate-300 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-600">Compare books</div>
        <div className="ledger-scroll overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <tr>
                {["Book", "Available Bal", "Freeze Bal", "Last day Difference", "Mismatches", "Settlement Avail"].map((h, i) => (
                  <th key={h} className={`px-4 py-2 font-bold whitespace-nowrap ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {books.map((b) => (
                <tr key={b.id} onClick={() => nav(`/book/${b.id}`)} data-testid={`dash-row-${b.id}`}
                  className="border-t border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5">
                    <p className="font-bold uppercase tracking-tight">{b.name}</p>
                    <div className="h-1.5 bg-slate-100 mt-1.5 w-full max-w-[180px]">
                      <div className={`h-full ${b.closing_available < 0 ? "bg-red-500" : "bg-cyan-500"}`} style={{ width: `${(Math.abs(b.closing_available) / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold">{fmt(b.closing_available)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmt(b.closing_freeze)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono tabular-nums font-semibold ${isZero(b.last_day_pnl) ? "text-green-700" : "text-red-700"}`}>{fmt(b.last_day_pnl)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`font-mono text-xs font-bold px-2 py-0.5 ${b.mismatches ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{b.mismatches}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-amber-800">{b.sett_entry_count ? fmt(b.sett_closing_available) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
